import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { Card, CardContent, Button, Eyebrow, RaceBib } from '@/components/foundry';
import { cn } from '@/lib/utils';

/**
 * FunnelAudienceCalculator — Foundations Module 02 · Lesson 02 ("Your numbers").
 * Route-family-aware funnel math: work backwards from the revenue target to the
 * monthly top-of-funnel volume each route requires. A planning anchor, not a
 * forecast — the route family is an `economics_hypothesis`, never a recommendation.
 */

type RouteKey = 'paid-slo' | 'paid-app' | 'dm-led' | 'organic' | 'subscription';

interface Stage { name: string; out: string; def: number; min: number; max: number }
interface RouteDef {
  key: RouteKey;
  label: string;
  unit: string; // top-of-funnel unit, per month
  dealLabel: string;
  dealDef: number;
  stages: Stage[];
}

const ROUTES: RouteDef[] = [
  {
    key: 'paid-slo', label: 'Paid · low-ticket', unit: 'visitors', dealLabel: 'Average order value', dealDef: 47,
    stages: [{ name: 'Visitor → buy', out: 'orders', def: 3, min: 0.5, max: 15 }],
  },
  {
    key: 'paid-app', label: 'Paid · application', unit: 'ad clicks', dealLabel: 'Average deal size', dealDef: 4000,
    stages: [
      { name: 'Click → application', out: 'applications', def: 8, min: 1, max: 30 },
      { name: 'Application → call', out: 'calls', def: 60, min: 20, max: 95 },
      { name: 'Call → close', out: 'clients', def: 25, min: 5, max: 60 },
    ],
  },
  {
    key: 'dm-led', label: 'DM-led · community', unit: 'conversations', dealLabel: 'Average deal size', dealDef: 1500,
    stages: [
      { name: 'DM → call', out: 'calls', def: 10, min: 2, max: 40 },
      { name: 'Call → purchase', out: 'clients', def: 30, min: 5, max: 70 },
    ],
  },
  {
    key: 'organic', label: 'Organic · waitlist', unit: 'people reached', dealLabel: 'Average deal size', dealDef: 2000,
    stages: [
      { name: 'Reach → opt-in', out: 'opt-ins', def: 3, min: 0.5, max: 20 },
      { name: 'Opt-in → buy', out: 'buyers', def: 5, min: 1, max: 30 },
    ],
  },
  {
    key: 'subscription', label: 'Membership', unit: 'trials', dealLabel: 'Annual value per member', dealDef: 1164,
    stages: [{ name: 'Trial → paid', out: 'members', def: 25, min: 3, max: 80 }],
  },
];

const LS_KEY = 'abl:labs:funnel-audience';
const usd = (n: number) => '$' + Math.round(n).toLocaleString('en-US');
const num = (n: number) => Math.round(n).toLocaleString('en-US');
const parseMoney = (s: string) => Number(String(s).replace(/[^0-9.]/g, '')) || 0;

async function captureLead(payload: Record<string, unknown>) {
  try {
    await fetch('/api/labs/lead', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch {
    /* best-effort */
  }
}

function ConfTag({ kind, children }: { kind: 'self' | 'anchor' | 'hypothesis' | 'computed'; children: ReactNode }) {
  const tone =
    kind === 'self' ? 'text-fd-teal border-fd-teal/30 bg-fd-teal/10'
      : kind === 'anchor' ? 'text-fd-accent-deep border-fd-accent/30 bg-fd-accent/10'
      : kind === 'hypothesis' ? 'text-fd-coral border-fd-coral/30 bg-fd-coral/10'
      : 'text-fd-text-muted border-fd-border bg-fd-surface-1';
  return (
    <span className={cn('inline-block rounded-[6px] border px-2 py-[3px] font-geist-mono text-[9.5px] font-medium uppercase tracking-[0.03em]', tone)}>
      {children}
    </span>
  );
}

function Slider({ value, min, max, step, onChange }: { value: number; min: number; max: number; step: number; onChange: (n: number) => void }) {
  const fill = ((value - min) / (max - min)) * 100;
  return (
    <input
      type="range" min={min} max={max} step={step} value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      style={{ '--fill': `${fill}%` } as CSSProperties}
      className="lt-range"
    />
  );
}

interface Props { saveMode?: 'profile' | 'email' }

export default function FunnelAudienceCalculator({ saveMode = 'profile' }: Props) {
  const [routeKey, setRouteKey] = useState<RouteKey>('paid-app');
  const [targetStr, setTargetStr] = useState('171,429');
  const [deal, setDeal] = useState(4000);
  const [convs, setConvs] = useState<number[]>(ROUTES.find((r) => r.key === 'paid-app')!.stages.map((s) => s.def));
  const [saved, setSaved] = useState(false);
  const [email, setEmail] = useState('');
  const [emailDone, setEmailDone] = useState(false);

  const route = ROUTES.find((r) => r.key === routeKey)!;

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(LS_KEY);
      if (!raw) return;
      const s = JSON.parse(raw) as Record<string, unknown>;
      if (typeof s.routeKey === 'string' && ROUTES.some((r) => r.key === s.routeKey)) {
        const rk = s.routeKey as RouteKey;
        setRouteKey(rk);
        const rd = ROUTES.find((r) => r.key === rk)!;
        setDeal(typeof s.deal === 'number' ? s.deal : rd.dealDef);
        setConvs(Array.isArray(s.convs) && s.convs.length === rd.stages.length ? (s.convs as number[]) : rd.stages.map((x) => x.def));
      }
      if (typeof s.targetStr === 'string') setTargetStr(s.targetStr);
    } catch {
      /* ignore */
    }
  }, []);
  useEffect(() => {
    try {
      window.localStorage.setItem(LS_KEY, JSON.stringify({ routeKey, targetStr, deal, convs }));
    } catch {
      /* ignore */
    }
  }, [routeKey, targetStr, deal, convs]);

  function selectRoute(rk: RouteKey) {
    const rd = ROUTES.find((r) => r.key === rk)!;
    setRouteKey(rk);
    setDeal(rd.dealDef);
    setConvs(rd.stages.map((s) => s.def));
    setSaved(false);
    setEmailDone(false);
  }
  const dirty = () => { if (saved) setSaved(false); };

  const model = useMemo(() => {
    const target = parseMoney(targetStr);
    const customersYr = deal > 0 ? target / deal : 0;
    const customersMo = customersYr / 12;
    const cumulative = convs.reduce((acc, c) => acc * (c / 100), 1);
    const front = cumulative > 0 ? customersMo / cumulative : 0;
    // funnel rows top → bottom
    const rows: { label: string; value: number }[] = [{ label: route.unit, value: front }];
    let running = front;
    route.stages.forEach((s, i) => {
      running = running * (convs[i] / 100);
      rows.push({ label: s.out, value: running });
    });
    return { front, customersMo, rows, lo: front * 0.8, hi: front * 1.2 };
  }, [targetStr, deal, convs, route]);

  return (
    <div className="lt-calc">
      <style>{`
        .lt-calc .lt-range{-webkit-appearance:none;appearance:none;width:100%;height:3px;border-radius:3px;background:var(--fd-surface-2);outline:0;margin:10px 0 0;cursor:pointer}
        .lt-calc .lt-range::-webkit-slider-runnable-track{height:3px;border-radius:3px;background:linear-gradient(to right,var(--fd-accent-deep) var(--fill,40%),var(--fd-surface-2) var(--fill,40%))}
        .lt-calc .lt-range::-moz-range-progress{height:3px;border-radius:3px;background:var(--fd-accent-deep)}
        .lt-calc .lt-range::-webkit-slider-thumb{-webkit-appearance:none;width:16px;height:16px;background:var(--fd-paper);border:2px solid var(--fd-accent-deep);border-radius:50%;margin-top:-7px;box-shadow:0 1px 2px rgba(20,16,8,0.18)}
        .lt-calc .lt-range::-moz-range-thumb{width:16px;height:16px;background:var(--fd-paper);border:2px solid var(--fd-accent-deep);border-radius:50%}
      `}</style>

      {/* header */}
      <div className="max-w-[60ch]">
        <div className="mb-4 flex items-center gap-3">
          <RaceBib n="02" tone="race" size="lg" shadow="sm" className="-rotate-3" />
          <Eyebrow>Module 02 · Lesson 02</Eyebrow>
        </div>
        <h1 className="font-geist text-[32px] font-medium leading-[1.05] tracking-[-0.022em] text-fd-text">Your numbers.</h1>
        <p className="mt-3 text-[15px] leading-[1.55] text-fd-text-muted">
          Pick how you get clients, drop in your revenue target, and we work backwards to the monthly
          volume it actually takes. Rough planning anchors, not a forecast.
        </p>
      </div>

      <div className="my-7 h-px w-full bg-fd-border/70" />

      <div className="mb-3 flex items-center gap-2">
        <Eyebrow>How you get clients</Eyebrow>
        <ConfTag kind="hypothesis">A starting guess, not our advice</ConfTag>
      </div>
      <div className="flex flex-wrap gap-2">
        {ROUTES.map((r) => {
          const active = r.key === routeKey;
          return (
            <button
              key={r.key} type="button" aria-pressed={active} onClick={() => selectRoute(r.key)}
              className={cn(
                'rounded-fd-sm border px-[15px] py-[9px] font-geist-mono text-[11px] font-medium uppercase tracking-[0.04em] transition-colors',
                active ? 'border-fd-accent-deep bg-fd-accent text-fd-text' : 'border-fd-border bg-fd-paper text-fd-text-muted hover:border-fd-text hover:text-fd-text',
              )}
            >
              {r.label}
            </button>
          );
        })}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.08fr)]">
        {/* INPUTS */}
        <Card>
          <CardContent className="pt-[22px]">
            <div className="mb-5 flex items-center justify-between">
              <Eyebrow>Your inputs</Eyebrow>
              <Eyebrow className="text-fd-text-subtle">Editable</Eyebrow>
            </div>

            <div className="mb-[22px]">
              <div className="mb-[9px] flex items-baseline justify-between gap-3">
                <span className="text-[13px] font-medium text-fd-text">Revenue target / year</span>
                <ConfTag kind="self">From your last tool</ConfTag>
              </div>
              <div className="flex items-center rounded-fd border border-fd-border bg-fd-surface-1 focus-within:border-fd-text">
                <span className="pl-[15px] pr-1 font-geist-mono text-[24px] font-medium text-fd-text-subtle">$</span>
                <input
                  inputMode="numeric" value={targetStr}
                  onChange={(e) => { setTargetStr(e.target.value); dirty(); }}
                  onBlur={(e) => { const n = parseMoney(e.target.value); setTargetStr(n ? n.toLocaleString('en-US') : ''); }}
                  aria-label="Annual revenue target"
                  className="w-full bg-transparent px-1 py-[11px] font-geist-mono text-[27px] font-medium tabular-nums tracking-[-0.01em] text-fd-text outline-none"
                />
              </div>
            </div>

            <div className="mb-[22px]">
              <div className="mb-[3px] flex items-baseline justify-between">
                <span className="text-[13px] font-medium text-fd-text">{route.dealLabel}</span>
                <span className="font-geist-mono text-[16px] font-medium tabular-nums text-fd-text">{usd(deal)}</span>
              </div>
              <Slider value={deal} min={Math.max(10, Math.round(route.dealDef * 0.1))} max={route.dealDef * 4} step={route.dealDef >= 1000 ? 50 : 1} onChange={(n) => { setDeal(n); dirty(); }} />
            </div>

            {route.stages.map((s, i) => (
              <div key={s.name} className={i === route.stages.length - 1 ? '' : 'mb-[22px]'}>
                <div className="mb-[3px] flex items-baseline justify-between">
                  <span className="text-[13px] font-medium text-fd-text">{s.name}</span>
                  <span className="font-geist-mono text-[16px] font-medium tabular-nums text-fd-text">{convs[i]}%</span>
                </div>
                <Slider
                  value={convs[i]} min={s.min} max={s.max} step={s.max <= 20 ? 0.5 : 1}
                  onChange={(n) => { setConvs((c) => c.map((x, j) => (j === i ? n : x))); dirty(); }}
                />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* RESULT */}
        <Card>
          <CardContent className="pt-[22px]">
            <Eyebrow className="flex items-center gap-2">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-fd-accent shadow-[0_0_0_3px_var(--fd-accent-soft)]" />
              You need, every month
            </Eyebrow>

            <div className="mt-2 w-fit">
              <div className="font-geist-mono text-[52px] font-semibold leading-none tracking-[-0.03em] tabular-nums text-fd-text">{num(model.front)}</div>
              <svg viewBox="0 0 300 9" preserveAspectRatio="none" className="-mt-0.5 block h-[9px] w-full">
                <path d="M2 6 C 70 2, 150 8, 298 4" stroke="var(--fd-accent)" strokeWidth={4} fill="none" strokeLinecap="round" />
              </svg>
            </div>
            <p className="mt-2 text-[14px] text-fd-text-muted">{route.unit} / month to hit your target</p>

            {/* funnel breakdown */}
            <div className="mt-6 overflow-hidden rounded-fd border border-fd-border/70">
              {model.rows.map((row, i) => (
                <div key={row.label} className={cn('flex items-center justify-between px-4 py-[11px]', i > 0 && 'border-t border-fd-border/70')}>
                  <span className="font-geist-mono text-[11px] uppercase tracking-[0.04em] text-fd-text-muted">{row.label} / mo</span>
                  <span className="font-geist-mono text-[18px] font-semibold tabular-nums text-fd-text">{num(row.value)}</span>
                </div>
              ))}
            </div>

            <div className="mt-[22px]">
              <div className="mb-[11px] flex items-baseline justify-between">
                <Eyebrow>If conversion moves ±20%</Eyebrow>
                <ConfTag kind="computed">Computed</ConfTag>
              </div>
              <div className="relative h-2 rounded-[5px] bg-fd-surface-2">
                <div className="absolute inset-y-0 left-[18%] right-[18%] rounded-[5px] bg-fd-teal/15" />
                <div className="absolute -inset-y-[3px] left-1/2 w-[3px] -translate-x-1/2 rounded-[2px] bg-fd-teal" />
              </div>
              <div className="mt-[9px] flex justify-between font-geist-mono text-[11px] text-fd-text-muted">
                <span>{num(model.hi)}</span>
                <span className="text-fd-teal">{num(model.front)} {route.unit}</span>
                <span>{num(model.lo)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* foot */}
      <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
        <p className="flex max-w-[54ch] items-start gap-2 text-[12.5px] text-fd-text-subtle">
          <span aria-hidden className="mt-px font-geist-mono">ⓘ</span>
          <span>Rough planning anchors, not a forecast. How you get clients is a guess to test, not our recommendation. Your AI co-founder sizes campaigns against these and flags when reality drifts.</span>
        </p>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => selectRoute(routeKey)}>Reset</Button>
          {saveMode === 'email' ? (
            emailDone ? (
              <Button variant="outline" className="border-fd-teal/40 bg-fd-teal/10 text-fd-teal" disabled>Sent — check your inbox</Button>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return;
                  void captureLead({ tool: 'funnel-audience', email, result: { route: routeKey, front: model.front, customersMo: model.customersMo } });
                  setEmailDone(true);
                }}
                className="flex items-center gap-2"
              >
                <input
                  type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com"
                  aria-label="Email to send your numbers"
                  className="h-8 rounded-fd-sm border border-fd-border bg-fd-paper px-3 font-geist text-[13px] text-fd-text outline-none focus:border-fd-text"
                />
                <Button type="submit" variant="primary">Email me my numbers</Button>
              </form>
            )
          ) : saved ? (
            <Button variant="outline" className="border-fd-teal/40 bg-fd-teal/10 text-fd-teal" disabled>Saved to your profile</Button>
          ) : (
            <Button variant="primary" onClick={() => setSaved(true)}>Save to my profile</Button>
          )}
        </div>
      </div>
    </div>
  );
}
